import React from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import swal from "sweetalert"
import classNames from "classnames"

import * as webSelectors from 'search-new/selectors'
import {
    storeCurrentGraph,
    resetVisualizationFilters,
    downloadVisualizationCSV,
    clearVisualizationSelection,
    deleteAllStoredGraphs,
    applyFiltersFromVisualization,
    updateExtraVisualizationData
} from "search-new/action-creators"

import { OrgDesignSelect } from "selects/containers/PieSelect"

import Tooltip from "components/Tooltip"

export const UnconnectedGenericSidebarButton = (props) => {
    // disable the button if the visualization is loading,
    // because there's nothing you can do with a non-existent
    // graph
    const disabled = props.disabled || props.isVisualizationLoading

    const onClick = () => {
        if (!disabled) {
            props.onClick(props.uniqueFlvId, props.uniqueVizSceneId)

            if (props.callback) {
                props.callback()
            }
        }
    }

    const button = (
        <button
            className={classNames(
                "btn",
                "btn-default-white",
                {
                    disabled: disabled,
                    "btn-block": !props.noBlock,
                    "no-top-border-radius": props.hasHeader,
                    active: props.active, // as in, it looks like it's pressed in
                    ...props.extraClasses
                }
            )}
            onClick={onClick}
        >
            {props.label}
            {props.icon && <i className={`fa button-i ${props.icon}`} />}
        </button>
    )

    const tooltipText = disabled
        ? props.disabledTooltipText || props.tooltipText
        : props.tooltipText

    return tooltipText
        ? <Tooltip label={tooltipText}>{button}</Tooltip>
        : button
}
UnconnectedGenericSidebarButton.propTypes = {
    onClick: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    noBlock: PropTypes.bool,
    icon: PropTypes.string,
    label: PropTypes.string.isRequired,
    tooltipText: PropTypes.string,
    disabledTooltipText: PropTypes.string,
    hasHeader: PropTypes.bool,
    isVisualizationLoading: PropTypes.bool,
    extraClasses: PropTypes.object,
}

const genericSidebarButtonMapStateToProps = (state, props) => ({
    isVisualizationLoading: webSelectors.selectIsVisualizationLoading(state, props)
})
export const GenericSidebarButton = connect(genericSidebarButtonMapStateToProps, null)(UnconnectedGenericSidebarButton)

export const GenericSidebarButtonGroup = (props) => {
    const renderButtons = () =>
        props.buttons.map((button) => {
            const ButtonComponent = button
            const key = button.WrappedComponent
                ? button.WrappedComponent.toString()
                : button.toString()

            return (
                <ButtonComponent
                    key={key}
                    uniqueFlvId={props.uniqueFlvId}
                    uniqueVizSceneId={props.uniqueVizSceneId}
                    disabled={props.disabled}
                    hasHeader={Boolean(props.groupLabel)}
                    noBlock
                />
            )
        })

    return (
        <div className="viz-button-group">
            {
                props.groupLabel &&
                <div className="header">
                    {props.groupLabel}
                </div>
            }
            <div className="btn-group variable-buttons">
                {renderButtons()}
            </div>
        </div>
    )
}
GenericSidebarButtonGroup.propTypes = {
    uniqueFlvId: PropTypes.string,
    uniqueVizSceneId: PropTypes.string,
    groupLabel: PropTypes.string,
    buttons: PropTypes.array.isRequired,
}

/* ***********************************************************************
* DOWNLOAD BUTTONS
************************************************************************ */

const DownloadGraphButton = (props) => {
    const onClick = () => {
        window.graphSVGDownload(`#${props.uniqueVizSceneId}`)
    }

    return (
        <GenericSidebarButton
            {...props}
            tooltipText='Download this graph'
            onClick={onClick}
            icon="fa-download"
            label={props.label || "Graph"}
        />
    )
}

const UnconnectedDownloadCSVButton = (props) => {
    const onClick = () => {
        swal('Nice!', 'Your download has begun.', 'success')

        props.downloadVisualizationCSV(props.uniqueFlvId, props.uniqueVizSceneId)
    }

    return (
        <GenericSidebarButton
            {...props}
            tooltipText='Download graph data in CSV'
            onClick={onClick}
            icon="fa-download"
            label={props.label || "CSV"}
        />
    )
}
const DownloadCSVButton = connect(null, { downloadVisualizationCSV })(UnconnectedDownloadCSVButton)

/* ***********************************************************************
* FILTER INTERACTION BUTTONS
************************************************************************ */

const vizSelectionMapStateToProps = (state, props) => ({
    selectedValues: webSelectors.selectVisualizationSelectedValues(state, props)
})
/* *************************************************************************/
const UnconnectedApplyFilterButton = (props) => {
    const onClick = () => {
        props.applyFiltersFromVisualization(props.uniqueFlvId, props.uniqueVizSceneId)
    }

    const active = props.selectedValues.length

    return (
        <GenericSidebarButton
            {...props}
            onClick={onClick}
            label={props.label || "Apply Selected"}
            disabled={!active}
            extraClasses={{ "clickable-highlight": active }}
            disabledTooltipText="Select graph items to refine your visualization"
            tooltipText="Click to apply selected items to filter"
        />
    )
}
const ApplyFilterButton = connect(vizSelectionMapStateToProps, { applyFiltersFromVisualization })(UnconnectedApplyFilterButton)

/* *************************************************************************/
const UnconnectedClearSelectionButton = (props) => {
    const onClick = () => {
        props.clearVisualizationSelection(props.uniqueFlvId, props.uniqueVizSceneId)
    }

    const active = props.selectedValues.length

    return (
        <GenericSidebarButton
            {...props}
            onClick={onClick}
            label={props.label || "Clear Selected"}
            extraClasses={{ "clickable-highlight": active }}
            disabled={!active}
        />
    )
}
const ClearSelectionButton = connect(vizSelectionMapStateToProps, { clearVisualizationSelection })(UnconnectedClearSelectionButton)

/* *************************************************************************/

const UnconnectedResetFiltersButton = (props) => {
    const onClick = () => {
        props.resetVisualizationFilters(props.uniqueFlvId, props.uniqueVizSceneId)
    }

    return (
        <GenericSidebarButton
            {...props}
            onClick={onClick}
            label={props.label || "Reset Filters"}
            disabled={!props.visualizationFiltersActive}
        />
    )
}
const resetFilterMapStateToProps = (state, props) => ({ visualizationFiltersActive: webSelectors.selectVisualizationFiltersActive(state, props) })
export const ResetFiltersButton = connect(resetFilterMapStateToProps, { resetVisualizationFilters })(UnconnectedResetFiltersButton)

/* ***********************************************************************
* VISUALIZATION LAYERING BUTTONS
************************************************************************ */

const UnconnectedSaveLayerButton = (props) => {
    const onClick = () => {
        props.storeCurrentGraph(props.uniqueFlvId, props.uniqueVizSceneId)
    }

    return (
        <GenericSidebarButton
            {...props}
            onClick={onClick}
            label={props.label || "Save Current Graph"}
        />
    )
}
export const SaveLayerButton = connect(undefined, { storeCurrentGraph })(UnconnectedSaveLayerButton)

/* *************************************************************************/
const UnconnectedDeleteAllLayersButton = (props) => {
    const onClick = () => {
        props.deleteAllStoredGraphs(props.uniqueFlvId, props.uniqueVizSceneId)
    }

    return (
        <GenericSidebarButton
            {...props}
            onClick={onClick}
            label={props.label || "Delete All Layers"}
        />
    )
}
export const DeleteAllLayersButton = connect(null, { deleteAllStoredGraphs })(UnconnectedDeleteAllLayersButton)

/* *************************************************************************/
const UnconnectedExtraDataButton = (props) => {
    const onClick = () => {
        props.updateExtraVisualizationData(props.uniqueFlvId, props.uniqueVizSceneId, props.extraData)
    }

    return (
        <GenericSidebarButton
            {...props}
            onClick={onClick}
        />
    )
}
const ExtraDataButton = connect(null, { updateExtraVisualizationData })(UnconnectedExtraDataButton)

/* *************************************************************************/

const UnconnectedBarPieExtraDataButton = (props) =>
    <ExtraDataButton
        {...props}
        active={props.currentGraph === props.graphKey}
        extraData={{ currentGraph: props.graphKey }}
    />
const BarPieExtraDataButton = connect(webSelectors.barPieProps, undefined)(UnconnectedBarPieExtraDataButton)

const BarExtraDataButton = (props) =>
    <BarPieExtraDataButton
        {...props}
        label="Bar"
        icon="fa-bar-chart"
        graphKey="bar"
    />

const PieExtraDataButton = (props) =>
    <BarPieExtraDataButton
        {...props}
        label="Pie"
        icon="fa-pie-chart"
        graphKey="pie"
    />

/* ***********************************************************************
* OTHER NOT QUITE BUTTONS BUT CLOSE ENOUGH!
************************************************************************ */
const UnconnectedOrgDesignSidebarComponent = (props) => {
    const changeDesign = (data) => {
        let designData = data
            ? {
                design: {
                    orgDesignId: data.id,
                    colorConfigs: {
                        primaryColor: data.primary_color,
                        secondaryColor: data.secondary_color,
                        noValueColor: data.light_grey,
                        darkerPrimaryColor: data.darker_primary,
                        lighterPrimaryColor: data.lighter_primary,
                    }
                }
            }
            : { design: null }

        props.updateExtraVisualizationData(props.uniqueFlvId, props.uniqueVizSceneId, designData)
    }

    return (
        <div className="viz-button-group">
            <OrgDesignSelect
                value={props.selectedDesignId}
                onChange={changeDesign}
                placeholder="Choose custom design..."
                dropUp
            />
        </div>
    )
}

const orgDesignMapStateToProps = (state, props) => ({
    selectedDesignId: webSelectors.selectVisualizationOrgDesignId(state, props)
})
export const OrgDesignSidebarComponent = connect(orgDesignMapStateToProps, { updateExtraVisualizationData })(UnconnectedOrgDesignSidebarComponent)

/* *************************************************************************/
const UnconnectedCurrentOnlyCheckbox = (props) => {
    const iconName = props.showCurrentOnly
        ? "fa-check-square-o"
        : "fa-square-o"

    const toggle = () => props.updateExtraVisualizationData(props.uniqueFlvId, props.uniqueVizSceneId, { currentOnly: !props.showCurrentOnly })

    return (
        <div className="icon-row" onClick={toggle}>
            <div className='text-container'>
                Show only current officials
            </div>
            <div className='icon-container'>
                <i className={`fa ${iconName}`}/>
            </div>
        </div>
    )
}

const currentOnlyMapStateToProps = (state, props) => ({
    showCurrentOnly: webSelectors.selectCurrentOnlyStatus(state, props)
})

export const CurrentOnlyCheckbox = connect(currentOnlyMapStateToProps, { updateExtraVisualizationData })(UnconnectedCurrentOnlyCheckbox)

/* ***********************************************************************
* BUTTON GROUPS!
************************************************************************ */
export const DownloadButtonGroup = (props) =>
    <GenericSidebarButtonGroup
        {...props}
        buttons={[DownloadGraphButton, DownloadCSVButton]}
    />

export const DelayedApplyFilterButtonGroup = (props) =>
    <GenericSidebarButtonGroup
        {...props}
        buttons={[ApplyFilterButton, ClearSelectionButton]}
    />

export const BarPieButtonGroup = (props) =>
    <GenericSidebarButtonGroup
        {...props}
        buttons={[BarExtraDataButton, PieExtraDataButton]}
    />
